import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import {ProjectStore} from "./project.store";
import {HttpClient} from "@angular/common/http";
import {Country} from "../shared/models/Country";
import {Residence} from "../data-access/api-residences/Residence";
import {catchError, finalize, Observable, of, tap} from "rxjs";
import {AppData} from "../shared/models/AppData";
import {Language} from "../shared/models/Language";
import {arrayUpsert} from "@datorama/akita";
import {Request} from "../data-access/api-folders/Folder";

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly baseUrl: string;
  private apiV1: string = environment.apiV1;
  private baseRef: string = environment.origin;
  private serviceCore: string = environment.service.core;

  constructor(
    private store: ProjectStore,
    private httpClient: HttpClient
  ) {
    this.baseUrl = environment.local;
  }

  public setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  public init(): void {
    this.setLoading(true);
    this.getLanguages$().subscribe();
    this.getProjectNationalities$().subscribe();
    this.getProjectCountries$().subscribe();
    this.getProjectResidences$().subscribe();
  }

  private getLanguages$(): Observable<any> {
    return this.getAvailableLanguages()
      .pipe(
        tap((languages: Language[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            languages
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getProjectNationalities$(): Observable<string[]> {
    return this.fetchNationalities()
      .pipe(
        tap((nationalities: string[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            nationalities
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getProjectCountries$() {
    return this.fetchCountries()
      .pipe(
        tap((countries: Country[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            countries
          }));
        }),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getProjectResidences$(): Observable<any> {
    return this.fetchResidences()
      .pipe(
        tap((response: Residence[]) => {
          this.store.update((state: AppData) => ({
            ...state,
            residences: response
          }));
        }),
        finalize(() => this.setLoading(false)),
        catchError((error) => {
          this.store.setError(error);
          return of(error);
        })
      );
  }

  private getAvailableLanguages = () => this.httpClient.get<Language[]>(
    `${this.baseUrl}/languages.json`
  );

  private fetchCountries = () => this.httpClient.get<Country[]>(
    `${this.baseUrl}/countries.json`
  );

  private fetchNationalities = () => this.httpClient.get<string[]>(
    `${this.baseUrl}/nationalities.json`
  );

  private fetchResidences = () => this.httpClient.get<Residence[]>(
    `${this.baseRef}${this.serviceCore}${this.apiV1}residences/public`
  );

  public upsertRequest(request: Request): void {
    this.store.update((state: AppData) => {
      const requests: Request[] = arrayUpsert(state.requests, request.id, request);
      return {
        ...state,
        requests
      };
    });
  }
}
